import { motion } from "framer-motion";
import { Video, ShieldCheck, TrendingUp, Layers, Film, Globe } from "lucide-react";
import BrandMarquee from "@/components/BrandMarquee";
import FAQSection from "@/components/FAQSection";
import CTASection from "@/components/CTASection";
import SectionLabel from "@/components/SectionLabel";
import ParallaxImage from "@/components/ParallaxImage";
import { UGC_FAQS, IMAGES } from "@/data/content";

const FORMATS = [
  "Product reviews",
  "Lifestyle content",
  "Testimonial-style videos",
  "Short-form social media content",
];

const BENEFITS = [
  { Icon: ShieldCheck, t: "Authentic & Trustworthy", d: "UGC feels real and relatable, helping you build stronger emotional connections with your audience." },
  { Icon: TrendingUp, t: "Better Performance", d: "Authentic creator content consistently outperforms traditional ads across paid and organic channels." },
  { Icon: Film, t: "High Engagement", d: "Storytelling and real-life product usage drive significantly higher engagement than polished ads." },
  { Icon: Layers, t: "Scalable Production", d: "Generate dozens of content assets without large production setups, studios or shoot days." },
  { Icon: Globe, t: "Versatile Usage", d: "Use UGC across social, paid ads, websites, landing pages and performance marketing." },
  { Icon: Video, t: "Multi-Format Ready", d: "Reels, shorts, testimonials, unboxings, lifestyle stories — all from a single brief." },
];

export default function UGC() {
  return (
    <div data-testid="ugc-page" className="bg-[#050505] text-[#F3E8E0]">
      {/* HERO */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 overflow-hidden grain-bg">
        <div className="absolute top-32 right-1/4 w-[500px] h-[500px] bg-[#FF4A00] opacity-10 rounded-full blur-[160px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              className="lg:col-span-8"
            >
              <SectionLabel className="mb-6">UGC at Incer</SectionLabel>
              <h1
                data-testid="ugc-hero-title"
                className="font-display text-4xl md:text-6xl lg:text-[6.25rem] font-semibold tracking-tighter leading-[0.92]"
              >
                Authentic UGC content that <span className="italic text-[#FF4A00] font-medium">drives results.</span>
              </h1>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-4"
            >
              <p className="text-lg text-[#A3A3A3] leading-relaxed">
                From product storytelling to performance-driven content, Incer enables brands to produce
                authentic, relatable content that connects — and converts.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHAT IS UGC */}
      <section className="py-24 md:py-32 border-t border-[#1a1a1a]" data-testid="ugc-what">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-6 relative">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-[#1a1a1a]">
                <ParallaxImage src={IMAGES.ugc} alt="UGC creator filming a skincare product review" className="absolute inset-0" intensity={0.14} />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#050505] via-transparent to-transparent z-10" />
              </div>
              <div className="absolute -bottom-6 -right-6 hidden md:block p-6 rounded-2xl bg-[#0a0a0a] border border-[#222] max-w-xs z-20">
                <div className="label-eyebrow mb-2">Did you know</div>
                <div className="font-display text-xl font-medium leading-snug">
                  UGC ads can deliver up to <span className="text-[#FF4A00]">4×</span> the click-through rate of traditional creatives.
                </div>
              </div>
            </div>
            <div className="lg:col-span-6">
              <SectionLabel className="mb-5">What is UGC?</SectionLabel>
              <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tighter leading-[0.95]">
                Real content. Real creators. Real impact.
              </h2>
              <p className="mt-6 text-lg text-[#A3A3A3] leading-relaxed">
                User-Generated Content (UGC) is content created by real creators that showcases your brand
                in a natural and relatable way. Unlike traditional ads, UGC focuses on authenticity —
                making it more engaging and trustworthy for audiences.
              </p>
              <div className="mt-8">
                <div className="label-eyebrow mb-4">UGC includes</div>
                <div className="flex flex-wrap gap-2">
                  {FORMATS.map((f, i) => (
                    <span
                      key={i}
                      data-testid={`ugc-format-${i}`}
                      className="px-4 py-2 rounded-full border border-[#222] bg-[#0a0a0a] text-sm text-[#F3E8E0] hover:border-[#FF4A00] hover:text-[#FF4A00] transition-colors"
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY UGC */}
      <section className="py-24 md:py-32 border-t border-[#1a1a1a] bg-[#070707]" data-testid="ugc-why">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-16 max-w-3xl">
            <SectionLabel className="mb-5">Why UGC works</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[0.95]">
              The performance edge of real content.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`ugc-benefit-${i}`}
                className="card-glow group p-8 border border-[#1a1a1a] rounded-2xl bg-[#0a0a0a] hover:border-[#FF4A00]/40 transition-colors"
              >
                <div className="w-11 h-11 rounded-xl bg-[#FF4A00]/10 flex items-center justify-center mb-6 group-hover:bg-[#FF4A00]/20 transition-colors">
                  <b.Icon className="w-5 h-5 text-[#FF4A00]" />
                </div>
                <h3 className="font-display text-xl font-medium tracking-tight mb-2">{b.t}</h3>
                <p className="text-[#A3A3A3] leading-relaxed">{b.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BRANDS STRIP */}
      <section className="py-16 border-y border-[#1a1a1a] bg-[#050505]" data-testid="ugc-brands">
        <div className="text-center mb-8 px-6">
          <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight">
            Creating content for leading brands across industries
          </h3>
        </div>
        <BrandMarquee speed={32} />
      </section>

      <CTASection
        testId="ugc-cta"
        eyebrow="UGC at scale"
        title="Need authentic content that converts? We've got the creators."
        description="From Meta ads to landing pages, our network of UGC creators produces content brands actually use."
      />
      <FAQSection items={UGC_FAQS} testId="ugc-faq" />
    </div>
  );
}
